/**
 * RemindersWidget: Shows upcoming reminders from all synced calendars.
 * Allows local dismiss/snooze; displays reminder method (email/notification).
 */

import React, { useMemo } from 'react';
import { format, formatDistanceToNow, addMinutes } from 'date-fns';
import { Bell, Clock, Trash2, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CalendarEvent, EventReminder } from '@/lib/eventStore';

interface Reminder {
  eventId: string;
  title: string;
  fireAt: Date;
  minutesBefore: number;
  method: string;
  source: string;
}

interface RemindersWidgetProps {
  events: CalendarEvent[];
  onDismiss?: (reminder: Reminder) => void;
  onSnooze?: (reminder: Reminder, minutes: number) => void;
}

export const RemindersWidget: React.FC<RemindersWidgetProps> = ({ events, onDismiss, onSnooze }) => {
  // Build list of upcoming reminders
  const reminders = useMemo<Reminder[]>(() => {
    const now = new Date();
    const upcoming: Reminder[] = [];

    // Safety check: ensure events is an array
    if (!Array.isArray(events)) {
      console.warn('[RemindersWidget] events is not an array:', events);
      return [];
    }

    events.forEach((event) => {
      if (event.status === 'cancelled' || !event.reminders?.length) return;

      event.reminders.forEach((reminder) => {
        const fireAt = addMinutes(event.start, -reminder.minutesBefore);
        // Show reminders from past due up to 24h in the future
        if (fireAt > addMinutes(now, -60) && fireAt < addMinutes(now, 1440)) {
          upcoming.push({
            eventId: event.id || '',
            title: event.title,
            fireAt,
            minutesBefore: reminder.minutesBefore,
            method: reminder.method,
            source: event.source,
          });
        }
      });
    });

    return upcoming.sort((a, b) => a.fireAt.getTime() - b.fireAt.getTime());
  }, [events]);

  if (reminders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-muted-foreground">
        <Bell className="w-8 h-8 mb-2 opacity-50" />
        <span className="text-sm">No upcoming reminders</span>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col gap-2 overflow-y-auto p-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
        Upcoming Reminders
      </div>

      {reminders.map((reminder, i) => {
        const isPastDue = reminder.fireAt < new Date();

        return (
          <div
            key={`${reminder.eventId}-${i}`}
            className={`flex items-start gap-2 p-2.5 rounded-lg border transition-colors ${
              isPastDue
                ? 'bg-red-50 border-red-200/50'
                : 'bg-blue-50 border-blue-200/50'
            }`}
          >
            <Bell className={`w-4 h-4 mt-0.5 shrink-0 ${isPastDue ? 'text-red-600' : 'text-blue-600'}`} />

            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold line-clamp-1">{reminder.title}</h4>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                <Clock className="w-3 h-3" />
                <span>
                  {isPastDue ? 'Due ' : ''} {formatDistanceToNow(reminder.fireAt, { addSuffix: true })}
                </span>
                <span className="text-xs px-1 py-0.5 rounded bg-background/60 border border-border/50">
                  {reminder.method}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => onSnooze?.(reminder, 5)}
                title="Snooze 5 minutes"
              >
                <RotateCw className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => onDismiss?.(reminder)}
                title="Dismiss"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
